/**
 * Servicio para autenticación por teléfono (OTP)
 */

import { supabase } from '../lib/supabaseClient';
import { normalizeToE164, detectInputType } from '../utils/phoneUtils';
import { logger } from '../utils/logger';

export interface PhoneOTPResponse {
  success: boolean;
  message?: string;
  expires_in_seconds?: number;
  error?: string;
}

export interface VerifyOTPResponse {
  success: boolean;
  user_id?: string;
  phone_e164?: string;
  email?: string | null;
  full_name?: string;
  user_created?: boolean;
  session?: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
  };
  error?: string;
}

export class PhoneAuthService {
  /**
   * Enviar código OTP por teléfono
   */
  static async sendOTP(phone: string): Promise<PhoneOTPResponse> {
    try {
      // Normalizar teléfono a formato E.164
      const phoneE164 = normalizeToE164(phone);
      
      if (!phoneE164) {
        return {
          success: false,
          error: 'Formato de teléfono inválido. Debe ser un número válido.',
        };
      }

      logger.debug('📱 Enviando OTP a:', phoneE164);

      // Invocar Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !anonKey) {
        if (import.meta.env.DEV) {
          console.error('[OTP] Faltan variables: VITE_SUPABASE_URL=', !!supabaseUrl, 'VITE_SUPABASE_ANON_KEY=', !!anonKey, '→ Revisa .env en la raíz del monorepo.');
        }
        throw new Error('Configuración de Supabase no encontrada. Revisa VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY.');
      }

      const sendUrl = `${supabaseUrl}/functions/v1/send-phone-otp`;
      if (import.meta.env.DEV) {
        console.log('[OTP] Enviando código →', sendUrl, '| phone_e164:', phoneE164);
      }
      const response = await fetch(sendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({ phone_e164: phoneE164 }),
      });

      let result: Record<string, unknown>;
      try {
        result = (await response.json()) as Record<string, unknown>;
      } catch {
        result = { error: 'Respuesta no JSON' };
      }

      if (import.meta.env.DEV) {
        console.log('[OTP] send-phone-otp → status:', response.status, '| body:', result);
      }

      if (!response.ok) {
        logger.error('❌ Error enviando OTP:', result);
        return {
          success: false,
          error: (result.error as string) || (result.message as string) || 'Error al enviar código OTP',
        };
      }

      logger.info('✅ OTP enviado exitosamente');
      if (import.meta.env.DEV) {
        console.log('[OTP] Si no te llega el código, revisa: Edge Function send-phone-otp (logs en Supabase), WhatsApp/Twilio y docs/VERIFICACION_OTP_ACCOUNTS.md');
      }
      return {
        success: true,
        message: (result.message as string) || 'Código OTP enviado exitosamente',
        expires_in_seconds: (result.expires_in_seconds as number) || 300,
      };
    } catch (error: any) {
      logger.error('❌ Error en sendOTP:', error);
      return {
        success: false,
        error: error.message || 'Error desconocido al enviar OTP',
      };
    }
  }

  /**
   * Envío híbrido: OTP al teléfono + un solo correo con magic link y OTP.
   * Usado en login "Sin contraseña" para que el usuario reciba enlace y código en el mismo email.
   */
  static async sendHybridOTP(
    phone: string,
    email: string,
    redirectTo: string
  ): Promise<PhoneOTPResponse> {
    try {
      const phoneE164 = normalizeToE164(phone);
      if (!phoneE164) {
        return { success: false, error: 'Formato de teléfono inválido.' };
      }
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !anonKey) {
        throw new Error('Configuración de Supabase no encontrada');
      }
      const hybridSendUrl = `${supabaseUrl}/functions/v1/send-phone-otp`;
      if (import.meta.env.DEV) {
        console.log('[OTP] Envío híbrido (código + email) →', hybridSendUrl);
      }
      const response = await fetch(hybridSendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
          'apikey': anonKey,
        },
        body: JSON.stringify({
          phone_e164: phoneE164,
          email: email.trim().toLowerCase(),
          redirect_to: redirectTo,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Error al enviar código',
        };
      }
      return {
        success: true,
        message: result.message || 'Código y enlace enviados',
        expires_in_seconds: result.expires_in_seconds || 300,
      };
    } catch (error: any) {
      logger.error('❌ Error en sendHybridOTP:', error);
      return {
        success: false,
        error: error.message || 'Error desconocido',
      };
    }
  }

  /**
   * Verificar código OTP y crear sesión.
   * Si se pasa email (flujo híbrido), se actualiza la cuenta existente con el nuevo teléfono en lugar de crear una nueva.
   */
  static async verifyOTP(phone: string, otpCode: string, email?: string): Promise<VerifyOTPResponse> {
    try {
      // Normalizar teléfono a formato E.164
      const phoneE164 = normalizeToE164(phone);
      
      if (!phoneE164) {
        return {
          success: false,
          error: 'Formato de teléfono inválido',
        };
      }

      if (!/^\d{6}$/.test(otpCode)) {
        return {
          success: false,
          error: 'Código OTP debe tener 6 dígitos',
        };
      }

      logger.debug('🔍 Verificando OTP:', { phone: phoneE164, codeLength: otpCode.length, hasEmail: !!email });

      // Invocar Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !anonKey) {
        throw new Error('Configuración de Supabase no encontrada');
      }

      const body: Record<string, string> = {
        phone_e164: phoneE164,
        otp_code: otpCode,
      };
      if (email && email.trim()) {
        body.email = email.trim().toLowerCase();
      }

      const verifyUrl = `${supabaseUrl}/functions/v1/verify-phone-otp`;
      console.log('[OTP] verify: URL', verifyUrl);
      const VERIFY_OTP_TIMEOUT_MS = 20000; // 20 s: evita que "Verificando código" quede colgado
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), VERIFY_OTP_TIMEOUT_MS);

      let response: Response;
      try {
        console.log('[OTP] verify: fetch start', verifyUrl);
        response = await fetch(verifyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
            'apikey': anonKey,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        console.log('[OTP] verify: fetch done', response.status, response.ok);
      } catch (fetchError: unknown) {
        clearTimeout(timeoutId);
        console.log('[OTP] verify: fetch error', fetchError);
        if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          return {
            success: false,
            error: 'La verificación tardó demasiado. Comprueba tu conexión e intenta de nuevo.',
          };
        }
        throw fetchError;
      }
      clearTimeout(timeoutId);

      // Leer cuerpo como texto con timeout 5s; luego parsear JSON (evita response.json() colgado)
      const BODY_READ_TIMEOUT_MS = 5000;
      console.log('[OTP] verify: response.text start (timeout', BODY_READ_TIMEOUT_MS, 'ms)');
      let rawText: string;
      try {
        rawText = await Promise.race([
          response.text(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('La lectura de la respuesta tardó demasiado. Intenta de nuevo.')), BODY_READ_TIMEOUT_MS)
          ),
        ]);
      } catch (bodyError: unknown) {
        console.log('[OTP] verify: response.text error o timeout', bodyError);
        throw bodyError;
      }
      console.log('[OTP] verify: response.text done, length', rawText?.length ?? 0);
      let result: any;
      try {
        result = rawText ? JSON.parse(rawText) : {};
      } catch (parseErr) {
        console.log('[OTP] verify: JSON.parse error', parseErr);
        throw new Error('Respuesta inválida del servidor. Intenta de nuevo.');
      }
      console.log('[OTP] verify: result parsed', !!result?.session);

      if (!response.ok) {
        logger.error('❌ Error verificando OTP:', result);
        return {
          success: false,
          error: result.error || 'Error al verificar código OTP',
        };
      }

      // ✅ Establecer sesión en Supabase usando tokens reales de Supabase Auth
      // Estos tokens fueron generados por Supabase Auth Admin API (generateLink)
      if (result.session?.access_token && result.session?.refresh_token) {
        console.log('[OTP] verify: setSession start');
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
        console.log('[OTP] verify: setSession done', !!sessionData?.session, !!sessionError);
        if (sessionError || !sessionData.session) {
          logger.error('❌ Error estableciendo sesión:', sessionError);
          return {
            success: false,
            error: 'Error al establecer sesión',
          };
        }

        logger.info('✅ Sesión de Supabase Auth establecida exitosamente', {
          userId: sessionData.user?.id,
          expiresAt: sessionData.session.expires_at,
        });
      }

      console.log('[OTP] verify: returning success');
      return {
        success: true,
        user_id: result.user_id,
        phone_e164: result.phone_e164,
        email: result.email,
        full_name: result.full_name,
        user_created: result.user_created || false,
        session: result.session,
      };
    } catch (error: any) {
      console.log('[OTP] verify: catch', error?.message ?? error);
      logger.error('❌ Error en verifyOTP:', error);
      return {
        success: false,
        error: error?.message || 'Error desconocido al verificar OTP',
      };
    }
  }

  /**
   * Detectar tipo de input (email o teléfono)
   */
  static detectInputType(input: string): 'email' | 'phone' | null {
    return detectInputType(input);
  }

  /**
   * Normalizar teléfono a E.164
   */
  static normalizePhone(phone: string): string | null {
    return normalizeToE164(phone);
  }
}
